import React, { createContext, useContext, useCallback, useReducer, useEffect, useState } from 'react'
import { useUser } from '@fs/zion-user'
import { useTranslation } from 'react-i18next'
import { useParams } from '@fs/zion-router'
import familyGroupReducer from '../components/familyGroupReducer'
import { filterAnswersByFamilyGroupSelection, getFilteredCategoryItems } from '../helpers/activities_helper'
import { useGetLeaderboard, useGetInvitations } from '../hooks/api'
import { getFamilyGroupsAndIndividuals } from '../helpers/family_helper'
import useIsComponentMounted from '../hooks/useIsComponentMounted'

const FamilyGroupContext = createContext()
export const useFamilyGroupContext = () => useContext(FamilyGroupContext)

const useDispatchFamilyGroups = ({ dispatch, data, state }) => {
  const isComponentMountedRef = useIsComponentMounted()
  const { t } = useTranslation()

  useEffect(() => {
    const needToSet = !!data && !(data?.items?.length === 0 && state.familyGroups?.length === 0)
    if (needToSet && isComponentMountedRef.current) {
      const familyGroups = data?.items
      const expandedGroups = getFamilyGroupsAndIndividuals({ familyGroups })
      dispatch({
        actionType: 'SET_FAMILY_GROUPS',
        value: { familyGroups: familyGroups?.length > 0 ? expandedGroups : [] },
      })
    }
  }, [data, dispatch, isComponentMountedRef, t, state.familyGroups?.length])
}

export default function FamilyGroupProvider({ children }) {
  const isComponentMountedRef = useIsComponentMounted()
  const initialValues = {
    familyGroup: null,
    familyGroups: null,
    showFamilyAnsweredActivities: true,
    showAddFamilyGroupPrompt: !localStorage.getItem('fs.discovery.hasShownAddFamilyGroupPrompt'),
  }
  const user = useUser()
  const [state, dispatch] = useReducer(familyGroupReducer, initialValues)
  const { cisId } = useParams()
  const [filteredSharedCategories, setFilteredSharedCategories] = useState([])
  const [sharedAnswers, setSharedAnswers] = useState([])
  const readyToLoadInvitations = state?.familyGroups?.length > 0
  const userReady = user?.signedIn
  const { query, data: familyGroupData } = useGetLeaderboard(userReady)
  const {
    data: invitationsData,
    query: { refetch: refetchInvitations },
  } = useGetInvitations(readyToLoadInvitations)

  const setFamilyGroup = (group) => {
    if (isComponentMountedRef.current) {
      dispatch({
        actionType: 'SET_FAMILY_GROUP',
        familyGroup: group,
      })
    }
  }

  useDispatchFamilyGroups({ dispatch, data: familyGroupData, state })

  const loadFilteredSharedAnswers = useCallback(
    ({ categories, returnAnswered }) => {
      if (!isComponentMountedRef.current) return
      if (categories && sharedAnswers) {
        const filteredCategoryItems = getFilteredCategoryItems({
          categories,
          answers: filterAnswersByFamilyGroupSelection(sharedAnswers, state?.familyGroup),
          returnAnswered,
        })
        if (isComponentMountedRef.current) setFilteredSharedCategories(filteredCategoryItems)
      }
    },
    [isComponentMountedRef, sharedAnswers, state?.familyGroup]
  )

  const setShowAddFamilyGroupPrompt = (value) => {
    if (isComponentMountedRef.current) {
      dispatch({
        actionType: 'SET_SHOW_FAMILY_GROUP_PROMPT',
        value,
      })
    }
  }

  let userReadyAndShowFamilyGroupPrompt = false
  if (state.showAddFamilyGroupPrompt && query?.isSuccess) {
    userReadyAndShowFamilyGroupPrompt = true
    localStorage.setItem('fs.discovery.hasShownAddFamilyGroupPrompt', true)
  }

  return (
    <FamilyGroupContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        ...state,
        userReadyAndShowFamilyGroupPrompt,
        cisId,
        setFamilyGroup,
        loadFamilyGroup: query?.refetch,
        loadFilteredSharedAnswers,
        setSharedAnswers,
        filteredCategories: filteredSharedCategories,
        sharedAnswers,
        invitations: invitationsData?.items?.length > 0 ? invitationsData?.items : [],
        refetchInvitations,
        setShowAddFamilyGroupPrompt,
      }}
    >
      {children}
    </FamilyGroupContext.Provider>
  )
}
